// AuthProvider.tsx
import { User } from '@/types/user';
import { fetchWithAuth, setIsUserLoggedIn } from '@/util/apiClient';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthContext';

async function fetchCurrentUser(): Promise<User | null> {
  try {
    const response = await fetchWithAuth('/auth/protected/');
    if (response.status === 401) {
      return null;
    }
    if (!response.ok) {
      return null;
    }
    return (await response.json()) as User;
  } catch (error) {
    // Silently handle auth errors on initial load
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: user,
    isLoading,
    error: _error,
  } = useQuery<User | null, Error>({
    queryKey: ['auth'],
    queryFn: fetchCurrentUser,
    retry: false, // Don't retry failed auth checks
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 4 * 60 * 1000, // Re-validate every 4 min (token expires at 5)
  });

  // Remove error logging to reduce console noise

  useEffect(() => {
    setIsUserLoggedIn(!!user);
  }, [user]);

  const refreshAuth = useCallback(async (): Promise<void> => {
    try {
      const userData = await fetchCurrentUser();
      queryClient.setQueryData(['auth'], userData);

      if (userData) {
        await queryClient.invalidateQueries();
      }

      setIsUserLoggedIn(!!userData);
    } catch (error) {
      if (error instanceof Error) {
        console.error('Error refreshing authentication:', error.message);
      } else {
        console.error('Unknown error during authentication refresh');
      }
    }
  }, [queryClient]);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await Promise.all([
        fetchWithAuth('/auth/logout/', { method: 'POST' }).catch((error) => {
          console.error(
            'Logout request failed:',
            error instanceof Error ? error.message : 'Unknown error',
          );
        }),
        queryClient.invalidateQueries(),
      ]);
    } catch (error) {
      console.error(
        'Error during logout:',
        error instanceof Error ? error.message : 'Unknown error',
      );
    }

    // Clear the user data, selected org, and redirect to login
    setIsUserLoggedIn(false);
    queryClient.setQueryData(['auth'], null);
    localStorage.removeItem('selectedOrg');
    navigate('/login');
  }, [queryClient, navigate]);

  const contextValue = useMemo(
    () => ({
      user: user ?? null, // Ensure undefined becomes null
      loading: isLoading,
      refreshAuth,
      logout,
    }),
    [user, isLoading, refreshAuth, logout],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}
